// src/pages/Question/Sidebar.jsx
import React, { useMemo, useState, useCallback } from 'react';
import ReadMoreTextBase from '@components/readmore/ReadMoreText';

const Sidebar = ({
  openQuestions,
  closedQuestions,
  groupedAdmins, // { A: [..], B: [..], ... }
  handleDeleteQuestion, // parent handler (invoked by modal confirm)
  handleAssignAdmin, // parent handler
  globalUser,
  globalData,
  userProfiles,
  defaultAvatarUrl,
  onStartAsk, // parent: start ask flow
  onSelectQuestion, // parent: select an existing question
  setSelectedQuestion, // parent: set modal "name"
  setSelectedQuestionId, // parent: set modal "id"
}) => {
  const [activeTab, setActiveTab] = useState('open');

  const flatAdmins = useMemo(() => {
    const list =
      Object.keys(groupedAdmins || {}) // 1. Take all the keys of groupedAdmins
        .sort() // 2. Sort the keys alphabetically
        .flatMap((k) => groupedAdmins[k] || []) || // 3. For each key, flatten its array of admins into one big array
      [];

    // 4. Sort the resulting flat list of admins alphabetically by their name
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groupedAdmins]);

  const formatDateTime = useCallback((ts) => {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const getAssignmentBadge = (q) => {
    const assignedId = q?.adminId || null;
    const isAssigned = Boolean(assignedId);

    //class="badge light badge-primary"
    //class="badge light badge-danger"

    // Non-admin (user) view
    if (!globalData?.isAdmin) {
      return (
        <span
          className={`badge badge-xs light ${
            isAssigned ? 'badge-primary' : 'badge-dark'
          }`}
        >
          {isAssigned ? 'Assigned' : 'Unassigned'}
        </span>
      );
    }

    // Admin view
    if (!isAssigned) {
      return (
        <span className='badge badge-xs light badge-danger'>Unassigned</span>
      );
    }

    const isMe = assignedId === globalUser?.uid;
    const adminName =
      userProfiles?.[assignedId]?.name || (isMe ? 'You' : 'Assigned'); // fallback if profile missing

    return (
      <span
        className={`badge badge-xs light ${
          isMe ? 'badge-primary' : 'badge-info'
        }`}
      >
        {isMe ? 'You' : adminName}
      </span>
    );
  };

  const renderQuestionItem = (q) => {
    const profile = userProfiles?.[q.userId] || {};
    const avatar = profile.avatar || defaultAvatarUrl;
    const name = profile.name || 'Unknown User';

    return (
      <div
        key={q.id}
        className='msg-bx d-flex justify-content-between align-items-center py-3 border-bottom'
      >
        {/* Left: whole row clickable */}
        <div
          className='msg d-flex align-items-center w-100'
          role='button'
          onClick={() => onSelectQuestion(q)}
        >
          <div className='image-box active'>
            <img src={avatar} />
          </div>

          <div className='ms-3 w-100'>
            <div className='d-flex justify-content-between align-items-center'>
              <h4 className='fs-14 font-w600 mb-1 text-truncate m-0'>
                <ReadMoreTextBase
                  text={q?.messages?.[0]?.text ?? 'No messages yet'}
                  readMore={false}
                  wordLimit={3}
                  plainText
                />
              </h4>
              <span className='ms-2'>{getAssignmentBadge(q)}</span>
            </div>

            <div className='d-flex justify-content-between align-items-center'>
              <span
                className='fs-12 me-auto text-truncate'
                style={{ maxWidth: '60%' }}
              >
                {name}
              </span>

              <span className='ms-3 fs-12 text-muted'>
                {formatDateTime(q.createdAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Right: kebab menu (do not trigger select) */}
        <div className='dropdown' onClick={(e) => e.stopPropagation()}>
          <button
            className='btn btn-link p-0 border-0'
            type='button'
            data-bs-toggle='dropdown'
            aria-expanded='false'
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle cx='12.5' cy='3.5' r='2.5' fill='#A5A5A5' />
              <circle cx='12.5' cy='11.5' r='2.5' fill='#A5A5A5' />
              <circle cx='12.5' cy='19.5' r='2.5' fill='#A5A5A5' />
            </svg>
          </button>

          <div className='dropdown-menu dropdown-menu-end'>
            {/* Delete opens parent-controlled modal */}
            <button
              type='button'
              className='dropdown-item'
              onClick={() => {
                setSelectedQuestion(name);
                setSelectedQuestionId(q.id);
              }}
              data-bs-toggle='modal'
              data-bs-target='#confirmDeleteQuestion'
            >
              Delete
            </button>

            {globalData?.isAdmin && (
              <button
                type='button'
                className='dropdown-item'
                onClick={() => {
                  setSelectedQuestionId(q.id);
                }}
                data-bs-toggle='modal'
                data-bs-target='#confirmAssignAdmin'
              >
                Assign to Me
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='h-100'>
      {/* Outer card — height/scroll controlled here */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '600px', // let parent column control height
          overflow: 'hidden', // prevent spill
        }}
      >
        {/* Header: NOT scrollable */}
        <div className='sidebar-header border-0 p-3 pb-0'>
          {!globalData?.isAdmin && (
            <div className='mb-3'>
              <button
                className='btn btn-primary w-100 btn-sm'
                onClick={onStartAsk}
              >
                <i className='mdi mdi-plus' /> Ask a new question...
              </button>
            </div>
          )}

          <ul className='nav nav-pills nav-justified bg-light rounded'>
            <li className='nav-item'>
              <button
                type='button'
                className={`nav-link ${activeTab === 'open' ? 'active' : ''}`}
                onClick={() => setActiveTab('open')}
              >
                Open
              </button>
            </li>
            <li className='nav-item'>
              <button
                type='button'
                className={`nav-link ${activeTab === 'closed' ? 'active' : ''}`}
                onClick={() => setActiveTab('closed')}
              >
                Closed
              </button>
            </li>
            <li className='nav-item'>
              <button
                type='button'
                className={`nav-link ${activeTab === 'admins' ? 'active' : ''}`}
                onClick={() => setActiveTab('admins')}
              >
                Admins
              </button>
            </li>
          </ul>
        </div>

        {/* Body: ONLY this area scrolls */}
        <div
          className='sidebar-list px-0'
          style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1, // take remaining height
            minHeight: 0, // allow flex overflow to scroll
            paddingBottom: 8, // small breathing room
          }}
        >
          {activeTab === 'open' && (
            <div className='px-3 pt-3'>
              {openQuestions?.length ? (
                openQuestions.map(renderQuestionItem)
              ) : (
                <div className='p-3 text-muted'>
                  No open questions available.
                </div>
              )}
            </div>
          )}

          {activeTab === 'closed' && (
            <div className='px-3 pt-3'>
              {closedQuestions?.length ? (
                closedQuestions.map(renderQuestionItem)
              ) : (
                <div className='p-3 text-muted'>
                  No closed questions available.
                </div>
              )}
            </div>
          )}

          {activeTab === 'admins' && (
            <ul className='list-unstyled mb-0 px-3 pt-3'>
              {flatAdmins?.length ? (
                flatAdmins.map((a) => (
                  <li key={a.id} className='chat-bx border-bottom py-3'>
                    <div className='d-flex align-items-center'>
                      <div className='chat-img me-2'>
                        <img
                          src={a.avatar || '/default-avatar.png'}
                          alt={a.name}
                        />
                      </div>
                      <div className='w-100'>
                        <span className='fs-16 text-black'>{a.name}</span>
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <div className='p-3 text-muted'>No admins available.</div>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
